import React, { useState } from "react";
import homepageData from "../../utils/data/homepage.json";
import { X, Loader2 } from "lucide-react";
import { createPaymentSession } from "../../utils/api";

const DonationPopup = ({ isOpen, onClose, preSelectedMission = "" }) => {
  const { missions } = homepageData;
  
  const [formData, setFormData] = useState({
    mission: preSelectedMission,
    amount: "",
    firstName: "",
    lastName: "",
    mobileNumber: "",
    email: "",
    address: ""
  });

  // Update mission when preSelectedMission changes
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      mission: preSelectedMission
    }));
    
    // Auto-fill amount based on selected mission
    if (preSelectedMission) {
      const selectedMission = missions.find(mission => mission.title === preSelectedMission);
      if (selectedMission && selectedMission.price > 0) {
        setFormData(prev => ({
          ...prev,
          amount: formatNumberWithCommas(selectedMission.price.toString())
        }));
      }
    }
  }, [preSelectedMission, missions]);

  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const formatNumberWithCommas = (value) => {
    // Remove all non-numeric characters
    const numericValue = value.replace(/[^\d]/g, '');
    // Add commas for thousands separators
    const formatted = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    console.log('Formatting:', value, '->', formatted); // Debug log
    return formatted;
  };

  const parseNumberFromFormatted = (formattedValue) => {
    // Remove commas and return numeric value
    return formattedValue.replace(/,/g, '');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-fill amount when mission is selected
    if (name === 'mission' && value) {
      const selectedMission = missions.find(mission => mission.title === value);
      if (selectedMission && selectedMission.price > 0) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          amount: formatNumberWithCommas(selectedMission.price.toString())
        }));
      } else if (selectedMission && selectedMission.price === 0) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          amount: ""
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else if (name === 'amount') {
      // Format the amount with commas for display
      console.log('Amount input changed:', value); // Debug log
      const formattedValue = formatNumberWithCommas(value);
      console.log('Formatted value:', formattedValue); // Debug log
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.mission) newErrors.mission = "Please select a mission";
    const numericAmount = parseNumberFromFormatted(formData.amount);
    if (!numericAmount || numericAmount <= 0) newErrors.amount = "Please enter a valid donation amount";
    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (!formData.mobileNumber) newErrors.mobileNumber = "Mobile number is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.address) newErrors.address = "Address is required";
    
    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    // Mobile validation
    if (formData.mobileNumber && !/^\d{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Please enter a valid 10-digit mobile number";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Cashfree Payment Integration
  const initializeCashfree = async () => {
    // Load Cashfree SDK
    const script = document.createElement('script');
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    script.onload = () => {
      window.Cashfree = window.Cashfree || {};
    };
    document.head.appendChild(script);
  };

  const createPaymentSessionData = async (amount) => {
    try {
      const data = {
        amount: amount,
        currency: 'INR',
        customer_details: {
          customer_id: `customer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          customer_name: `${formData.firstName} ${formData.lastName}`,
          customer_email: formData.email,
          customer_phone: formData.mobileNumber,
        },
        order_meta: {
          return_url: window.location.origin,
          notify_url: window.location.origin + '/api/payment-webhook',
        }
      };

      return await createPaymentSession(data);
    } catch (error) {
      console.error('Error creating payment session:', error);
      throw error;
    }
  };

  const processPayment = async () => {
    try {
      setIsProcessing(true);
      setPaymentStatus('processing');

      // Initialize Cashfree
      await initializeCashfree();

      // Parse amount without commas for payment processing
      const numericAmount = parseNumberFromFormatted(formData.amount);

      // Create payment session
      const paymentSession = await createPaymentSessionData(numericAmount);

      // Initialize Cashfree payment
      const cashfree = new window.Cashfree({
        mode: "production" // Using production mode
      });

      const checkoutOptions = {
        paymentSessionId: paymentSession.payment_session_id,
        returnUrl: `${window.location.origin}?order_id=${paymentSession.order_id}&amount=${numericAmount}`,
        onSuccess: (data) => {
          console.log('Payment successful:', data);
          setPaymentStatus('success');
        },
        onFailure: (data) => {
          console.log('Payment failed:', data);
          setPaymentStatus('failed');
        }
      };

      await cashfree.checkout(checkoutOptions);

    } catch (error) {
      console.error('Payment error:', error);
      setPaymentStatus('failed');
      
      // Redirect to failed page with error details
      const errorMessage = error.message || 'Payment processing failed';
      const orderId = `error_${Date.now()}`;
      const numericAmount = parseNumberFromFormatted(formData.amount);
      window.location.href = `${window.location.origin}?order_id=${orderId}&amount=${numericAmount}&status=failed&reason=${encodeURIComponent(errorMessage)}`;
    } finally {
      setIsProcessing(false);
    }
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      processPayment();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-source-serif-4 font-semibold text-[#67142D]">
            Join Our Mission
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Mission Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Mission *
            </label>
            <select
              name="mission"
              value={formData.mission}
              onChange={handleInputChange}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#67142D] focus:border-transparent ${
                errors.mission ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Choose a mission to support</option>
              {missions.map((mission, index) => (
                <option key={index} value={mission.title}>
                  {mission.title}
                </option>
              ))}
            </select>
            {errors.mission && (
              <p className="text-red-500 text-sm mt-1">{errors.mission}</p>
            )}
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Donation Amount (₹) *
            </label>
            {(() => {
              const selectedMission = missions.find(mission => mission.title === formData.mission);
              const isFixedPrice = selectedMission && selectedMission.price > 0;
              const isCustomAmount = selectedMission && selectedMission.price === 0;
              
              return (
                <input
                  type="text"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder={isCustomAmount ? "Enter your donation amount" : "Amount will be set automatically"}
                  disabled={isFixedPrice}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#67142D] focus:border-transparent ${
                    errors.amount ? "border-red-500" : "border-gray-300"
                  } ${isFixedPrice ? "bg-gray-100 cursor-not-allowed" : ""}`}
                />
              );
            })()}
            {(() => {
              const selectedMission = missions.find(mission => mission.title === formData.mission);
              if (selectedMission && selectedMission.price > 0) {
                return (
                  <p className="text-sm text-gray-600 mt-1">
                    Fixed amount for this mission
                  </p>
                );
              } else if (selectedMission && selectedMission.price === 0) {
                return (
                  <p className="text-sm text-gray-600 mt-1">
                    Enter your desired donation amount
                  </p>
                );
              }
              return null;
            })()}
            {errors.amount && (
              <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
            )}
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name *
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="Enter first name"
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#67142D] focus:border-transparent ${
                  errors.firstName ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.firstName && (
                <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name *
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Enter last name"
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#67142D] focus:border-transparent ${
                  errors.lastName ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.lastName && (
                <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Contact Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number *
              </label>
              <input
                type="tel"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleInputChange}
                placeholder="Enter 10-digit mobile number"
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#67142D] focus:border-transparent ${
                  errors.mobileNumber ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.mobileNumber && (
                <p className="text-red-500 text-sm mt-1">{errors.mobileNumber}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter email address"
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#67142D] focus:border-transparent ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address *
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Enter your complete address"
              rows={3}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#67142D] focus:border-transparent ${
                errors.address ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.address && (
              <p className="text-red-500 text-sm mt-1">{errors.address}</p>
            )}
          </div>

          {/* Payment Status */}
          {paymentStatus === 'processing' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center">
                <Loader2 className="animate-spin h-5 w-5 text-blue-600 mr-2" />
                <span className="text-blue-800">Processing payment...</span>
              </div>
            </div>
          )}

          {paymentStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-center">
                <span className="text-green-800">Payment successful! Thank you for your donation.</span>
              </div>
            </div>
          )}

          {paymentStatus === 'failed' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center">
                <span className="text-red-800">Payment failed. Please try again.</span>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing}
              className="px-6 py-3 bg-[#67142D] text-white rounded-lg hover:bg-[#B51F3B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  Processing...
                </>
              ) : (
                `Donate ₹${formData.amount || '0'}`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DonationPopup;
